import React from 'react';
import {Table} from 'web-components';
import {useIntl} from 'react-intl';
import Util from '../../../../forecast-app/shared/util/util';
import {ProjectStatusColor, TextColumn} from './ProjectPortfolioReport.styled';
import Person from '../../../../forecast-app/shared/components/person/person';
import {BUDGET_TYPE, profilePicSrc, PROJECT_STATUS, ESTIMATION_UNIT} from '../../../../constants';
import TooltipContainer from '../../../../forecast-app/shared/components/tooltips/tooltip_container';
import {ProjectContact} from './ProjectContact.styled';
import util from '../../../../forecast-app/shared/util/util';
import {ProjectPortfolioReportTooltips} from '../ProjectPortfolioReportTooltips';
import LabelGroup from '../../../../forecast-app/shared/components/labels/label_group';
import {CurrencyColumn} from '../CurrencyColumn';

export const renderStatus = status => {
	if (status) {
		return <ProjectStatusColor style={{backgroundColor: status.color}} />;
	} else {
		return null;
	}
};

export const renderStage = stage => {
	return <TextColumn>{Util.getProjectStageTranslation(stage, useIntl())}</TextColumn>;
};

export const renderRateCard = rateCard => {
	return <TextColumn>{rateCard && rateCard.name}</TextColumn>;
};

export const renderClient = client => {
	return <TextColumn>{client && client.name}</TextColumn>;
};

export const renderDate = date => {
	let dateString = '';
	if (date) {
		dateString = `${useIntl().formatDate(date.toDate(), {year: 'numeric', month: 'short', day: '2-digit'})}`;
	}
	return dateString;
};

export const renderOwners = people => {
	let contactPersons = [];
	if (people && people.edges) {
		contactPersons = people.edges
			.map(edge => edge.node)
			.filter(projectPerson => projectPerson.isContactPerson)
			.map(projectPerson => projectPerson.person);
	}

	return (
		<>
			{contactPersons.map((contactPerson, index) => (
				<ProjectContact key={index}>
					<TooltipContainer translatedMessage={true} infoText={contactPerson.fullName} timer={5 * 60 * 1000}>
						<Person
							name={contactPerson.fullName}
							imageSrc={contactPerson.profilePictureId ? profilePicSrc(contactPerson.profilePictureId) : null}
							showName={contactPersons.length === 1}
							showRole={false}
							hideTitle={true}
						/>
					</TooltipContainer>
				</ProjectContact>
			))}
		</>
	);
};

export const renderBudgetType = value => {
	let label = '';
	const intl = useIntl();
	if (value) {
		const {budgetType, defaultPeriodBudgetType} = value;
		label = Util.getBudgetTypeTranslation(budgetType, intl);
		if (budgetType === BUDGET_TYPE.RETAINER && defaultPeriodBudgetType) {
			label += ` (${Util.getPeriodBudgetTypeTranslation(defaultPeriodBudgetType, intl)})`;
		}
	}
	return <div>{label}</div>;
};

export const renderLabels = value => {
	if (value) {
		const labels = value.edges.map(e => e.node);
		return <LabelGroup labels={labels} noWrap={true} />;
	} else {
		return null;
	}
};

export const renderPercentage = percentage => {
	if (percentage) {
		return Util.getFormattedPercentage(percentage, useIntl());
	} else {
		return '–';
	}
};

export const getProjectData = rowData => {
	const projectData = rowData.project ? rowData.project : {};

	projectData.startDate = util.CreateNonUtcMomentDate(
		projectData.projectStartYear,
		projectData.projectStartMonth,
		projectData.projectStartDay
	);
	projectData.endDate = util.CreateNonUtcMomentDate(
		projectData.projectEndYear,
		projectData.projectEndMonth,
		projectData.projectEndDay
	);
	return projectData;
};

const ProjectPortfolioPeriodRow = ({rowData, tableColumnProps, headerColumn, canExpand, hasBaseline}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const financialData = rowData.financialNumbers;
	const projectData = getProjectData(rowData);
	const hasLowerWinChance =
		hasBaseline &&
		rowData.project?.useBaseline &&
		rowData.project?.status === PROJECT_STATUS.OPPORTUNITY &&
		rowData.project?.baselineWinChance < 1;
	const isEstimatedInPoints = rowData.project?.estimationUnit === ESTIMATION_UNIT.POINTS;

	const renderMinutes = minutes => {
		if (minutes) {
			return Util.convertMinutesToFullHour(minutes, intl);
		} else {
			return '–';
		}
	};

	const isFixedPriceProject = () => {
		return [BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2].includes(projectData.budgetType);
	};

	if (canExpand !== undefined) {
		tableColumnProps.canExpand = canExpand;
	}
	return (
		<Table.Row {...tableColumnProps}>
			<Table.Column>{headerColumn}</Table.Column>
			{/* Project Overview */}
			<Table.Column>{renderPercentage(projectData.baselineWinChance)}</Table.Column>
			<Table.Column>{renderDate(projectData.startDate)}</Table.Column>
			<Table.Column>{renderDate(projectData.endDate)}</Table.Column>
			<Table.Column>{renderClient(projectData.client)}</Table.Column>
			<Table.Column>{renderStatus(projectData.currentProjectStatus)}</Table.Column>
			<Table.Column>{renderOwners(projectData.projectPersons)}</Table.Column>
			<Table.Column>{renderStage(projectData.status)}</Table.Column>
			<Table.Column>{renderMinutes(financialData.allocationMinutes)}</Table.Column>
			<Table.Column>{renderBudgetType(projectData)}</Table.Column>
			<Table.Column>{renderRateCard(projectData.rateCard)}</Table.Column>
			<Table.Column>{renderPercentage(projectData.completion / 100)}</Table.Column>
			<Table.Column>{renderPercentage(projectData.progressEndOfTheMonth / 100)}</Table.Column>
			{/* Project Details */}
			<Table.Column>{renderMinutes(financialData.registeredMinutes)}</Table.Column>
			<Table.Column>{renderMinutes(financialData.remainingMinutes)}</Table.Column>
			<Table.Column>{renderMinutes(financialData.forecastMinutes)}</Table.Column>
			<Table.Column>{renderMinutes(financialData.scopeTotalMinutes)}</Table.Column>
			<Table.Column>{renderMinutes(financialData.scopeApprovedMinutes)}</Table.Column>
			<Table.Column>{renderMinutes(financialData.baselineMinutes)}</Table.Column>
			{/* Baseline */}
			<CurrencyColumn value={financialData.baselineRevenue} currency={rowData.currency} />
			<CurrencyColumn value={financialData.baselineCost} currency={rowData.currency} />
			<CurrencyColumn value={financialData.baselineProfit} currency={rowData.currency} />
			<Table.Column>{renderPercentage(financialData.baselineMargin)}</Table.Column>
			{/* Invoiced */}
			<CurrencyColumn value={tableColumnProps.level > 1 ? 0 : financialData.invoiced} currency={rowData.currency} />
			<CurrencyColumn value={financialData.paid} currency={rowData.currency} />
			{/* Revenue Recognition */}
			<CurrencyColumn
				value={financialData.lockedRevenue}
				tooltip={ProjectPortfolioReportTooltips.RecognitionLockedRevenueTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialData.recognitionOpenRevenue}
				tooltip={ProjectPortfolioReportTooltips.RecognitionOpenRevenueTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialData.recognitionForecastRevenue}
				tooltip={ProjectPortfolioReportTooltips.RecognitionForecastRevenueTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			{/* Fixed Price */}
			<CurrencyColumn value={isFixedPriceProject() ? projectData.budgetBaseCurrency : null} currency={rowData.currency} />
			{/* Actuals */}
			<CurrencyColumn
				value={hasLowerWinChance ? 0 : financialData.actualRevenue}
				hasLowerWinChance={hasLowerWinChance}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.ActualLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={hasLowerWinChance ? 0 : financialData.actualCost}
				hasLowerWinChance={hasLowerWinChance}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.ActualLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn value={financialData.actualProfit} currency={rowData.currency} />
			<Table.Column>{renderPercentage(financialData.actualMargin)}</Table.Column>
			{/* Planned */}
			<CurrencyColumn
				value={financialData.plannedRevenue}
				hasLowerWinChance={hasLowerWinChance && !isEstimatedInPoints}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.PlanRevenueLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialData.plannedCost}
				hasLowerWinChance={hasLowerWinChance && !isEstimatedInPoints}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.PlanCostLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn value={financialData.plannedProfit} currency={rowData.currency} />
			<Table.Column>{renderPercentage(financialData.plannedMargin)}</Table.Column>
			{/* Remaining */}
			<CurrencyColumn
				value={financialData.remainingRevenue}
				hasLowerWinChance={hasLowerWinChance}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.RemainingRevenueLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialData.remainingCost}
				hasLowerWinChance={hasLowerWinChance}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.RemainingCostLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn value={financialData.remainingProfit} showZero={true} currency={rowData.currency} />
			<Table.Column>{renderPercentage(financialData.remainingMargin)}</Table.Column>
			{/* Forecasted */}
			<CurrencyColumn
				value={financialData.forecastRevenue}
				hasLowerWinChance={hasLowerWinChance}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.ForecastRevenueLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn
				value={financialData.forecastCost}
				hasLowerWinChance={hasLowerWinChance}
				lowerWinChanceTooltip={ProjectPortfolioReportTooltips.ForecastCostLowerWinChanceTooltip(formatMessage)}
				currency={rowData.currency}
			/>
			<CurrencyColumn value={financialData.forecastProfit} showZero={true} currency={rowData.currency} />
			<Table.Column>{renderPercentage(financialData.forecastMargin)}</Table.Column>
			{/* Actual vs Plan */}
			<CurrencyColumn value={financialData.actualVsPlanRevenue} showZero={true} currency={rowData.currency} />
			<CurrencyColumn value={financialData.actualVsPlanCost} showZero={true} currency={rowData.currency} />
			<CurrencyColumn value={financialData.actualVsPlanProfit} showZero={true} currency={rowData.currency} />
			{/* Forecasted vs Plan */}
			<CurrencyColumn value={financialData.forecastVsPlanRevenue} showZero={true} currency={rowData.currency} />
			<CurrencyColumn value={financialData.forecastVsPlanCost} showZero={true} currency={rowData.currency} />
			<CurrencyColumn value={financialData.forecastVsPlanProfit} showZero={true} currency={rowData.currency} />
			{/* Invoiced vs. Actual */}
			<CurrencyColumn value={financialData.invoiced} showZero={true} currency={rowData.currency} />
			<CurrencyColumn value={financialData.actualRevenue} showZero={true} currency={rowData.currency} />
			<CurrencyColumn value={financialData.invoicedVsActualRevenue} showZero={true} currency={rowData.currency} />
			{/* Labels */}
			<Table.Column>{renderLabels(projectData.projectLabels)}</Table.Column>
			{/* Retainer period target */}
			<Table.Column>{renderMinutes(financialData.retainerPeriodTargetMinutes)}</Table.Column>
			<CurrencyColumn value={financialData.retainerPeriodTargetPrice} showZero={true} currency={rowData.currency} />
		</Table.Row>
	);
};

export default ProjectPortfolioPeriodRow;
